<template>
  <layout-public>
    <page-meta />

    <!-- The hero is the section's, not the project's. Every case page opens on
         the same two lines, "Case Study" over one generic sentence, because the
         page is a template and it is the work below that varies. The project is
         named twice without the hero having to name it: the crumb above carries
         the domain, and the band below opens on the client's own name.

         The hero carries no `link`. That slot is the website review's address,
         the site that report is about, and it was carrying this page's live URL
         until it was dropped. The live address is not hidden: the preview band
         below frames the client's site and offers the same address as a button. -->
    <page-hero
      :crumbs="crumbs"
      :title="t('Case Study')"
      :lede="t('A closer look at one of the websites we designed and built: what the project needed, what we made, and the decisions behind it.')"
    />

    <!-- 1. What this is, in one band: the work, the client, the design language
         and the build. It was four sections, and a section head each time ("The
         screens", "Built with", "Design language") said nothing the rows do not
         say themselves, so the heads are gone and the band is a sheet read from
         the top.

         The row is the band's unit and the group is the sheet's. Each row is one
         subject and a hairline separates them, because different things stacked
         with nothing between them read as one pile, and the sheet's four groups
         are divided the same way: the cross-ruling is what makes them quarters of
         one plate rather than a second pile of two rows. The work comes first, so
         the spec below it is read by someone who has already seen what it
         describes, and it is the one row with no label: screens of the client's
         own site cannot be mistaken for anything else.

         The captures are supplied per project and the band is drawn whether or
         not they have arrived, so a case page always shows the same three
         devices. A device with a capture is a device; one without is the same
         empty slot the frames below draw, at the device's own size, so the row
         keeps its shape and the missing picture reads as a picture that has not
         arrived rather than as a screen that failed. -->
    <section class="case-glance">
      <div class="container">
        <div class="case-row case-row--lead">
          <device-showcase :devices="deviceShots" data-aos="fade-up" />
        </div>

        <!-- The code that opens the live site on a phone, at the head of the
             column and above the name. A case page is read at a desk and the site
             it is about is a phone-sized experience, so the code is the one thing
             the page can offer that the frame below it cannot: the address in a
             hand. It leads the column because that is the order it is used in, and
             because the row under it is about to say who the client is, which is
             the context the address is worth having. It carries the reveal
             itself, as the stack member directly above the title.

             `v-if` on the path rather than an empty slot, which is the one place
             this page breaks its own rule. Every other unsupplied asset here is
             drawn as an empty frame so the band keeps its shape while the artwork
             arrives. A code cannot be: a frame with nothing in it still says
             "screenshot goes here", and a square with nothing in it says nothing
             and cannot be scanned. So a project without a code simply shows the
             client row without one, and the page is whole either way.

             The client. The name is the one string on the page that is neither
             translated nor written by us.

             The categories follow the case sentence rather than sitting between
             the name and it. They are the catalog's own badges, the same words
             the card prints under a screenshot, set as chips, and a badge row is
             a summary of the work: a summary only reads as one once the sentence
             it summarises has been said. Before the sentence it was a prefix to a
             name, which is a different job the chips were not doing.

             The row is the shared post-lede row, not a page-local margin, so it
             takes the same step every other row under a lede on the site takes.

             The facts are the shared panel in its paired layout: a ruled ledger of
             a label over the value it names. They were a hand-rolled `dl` with its
             own label treatment, which had drifted to the one label on the site
             that read below the contrast floor.

             It is the dark panel rather than the light one, and the dark panel
             here is InkSlab: the same ink slab with the corner glow that the promo
             details pages open their brief with and the brand-order sheet carries,
             so this page's dark moment is the one the site already has rather than
             a third way of being dark. It is the arrangement those pages use too,
             a `section-title` inside the slab with a `bare` dark panel under it.

             `bare` because the slab owns the surface and the glow: a panel that
             painted its own fill here would be a second surface inside the first,
             and a dark tone is a 5% film, so on its own it is white type on a
             near-white sheet. The slab is what the film needs behind it.

             The two columns are the band's own, so the client opens the row and
             the sheet closes it, and the marks are the slab family's own: the
             2.1rem outline tile the promo brief and the order sheet use, because a
             solid blue square inside an ink slab reads as a control rather than a
             bullet.

             They are centred against each other rather than started together. The
             client's column is the taller of the two on every project, because it
             carries the QR card, the name, the sentence and the chips, and the
             ledger beside it is five or six rows: started together, the ledger
             hangs off the top of the slab with the whole difference standing as
             air under it, and the two read as one long column with a note beside
             its first half. Centred, they read as the two halves of one
             arrangement. The slab then centres the pair on its own, because its
             padding is symmetric and the taller column is what sets the row's
             height, so the vertical centring is the row's alignment and not a
             margin anyone has to keep in step with the padding.

             The row carries no reveal of its own, and that is the family's
             arrangement rather than an omission: a PanelList's rows are their own
             reveal units, so a block that revealed as well would run the two
             travels together. The title takes the reveal instead, the way the
             order sheet's does, and the ledger's rows arrive one by one.

             Each fact carries its mark because the ledger is read at a glance the
             way the build rows below it are. They are field marks rather than
             decoration: they say what kind of thing the value is before the label
             is read.

             One value leaves the page, so it is supplied through the panel's
             `value` slot as the site's inline link: the same treatment a "read
             more" uses, with the same outward arrow the visit buttons carry, so
             the value that leaves the page reads the way every other value that
             leaves the page does. -->
        <div class="case-row case-row--slab">
          <ink-slab>
            <div class="row g-5 align-items-center">
              <div class="col-lg-6">
                <qr-card
                  v-if="project.qr"
                  class="case-qr"
                  data-aos="fade-up"
                  :src="project.qr"
                  :url="project.url"
                />

                <section-title
                  align="left"
                  tone="dark"
                  data-aos="fade-up"
                  :kicker="t('The client')"
                  :title="project.fact.client"
                  :lede="caseCopy"
                >
                  <div class="action-row">
                    <tag-list tone="dark" :tags="badges" />
                  </div>
                </section-title>
              </div>

              <div class="col-lg-6">
                <panel-list
                  as="aside"
                  layout="pairs"
                  tone="dark"
                  bare
                  mark-size="sm"
                  mark-variant="outline"
                  :title="t('The brief')"
                  :items="facts"
                >
                  <template #value="{ item }">
                    <app-button
                      v-if="item.href"
                      variant="link"
                      tone="dark"
                      :href="item.href"
                      target="_blank"
                      rel="noopener"
                      icon="arrow-up-right-from-square"
                      icon-after
                    >
                      {{ item.value }}
                    </app-button>

                    <template v-else>{{ item.value }}</template>
                  </template>
                </panel-list>
              </div>
            </div>
          </ink-slab>
        </div>

        <!-- The spec sheet, as one plate of four groups rather than two rows of
             two. It states the design language and the build: the faces it is set
             in, the colours it is built from, the stack it runs on and the tools
             it was drawn in. The palette is the same SwatchList the brand kit
             sheet shows, so a colour is the same object on both pages, and here
             it carries the colour's name as well as its code because a case study
             is read by people rather than handed to a printer. The faces are
             samples rather than chips: a name says which typeface the build was
             set in, and the letterforms are what that means.

             The four are one plate because they are one kind of thing. Two rows
             of two read as two statements with a gap between them, which is what
             they were, and nothing about the four says which pair belongs with
             the other. Divided in both directions they read as what they are: the
             four quarters of one specification, cross-ruled, with nothing
             between the groups but the rules that divide them.

             The sheet is groundless, which is the decision rather than the
             absence of one. The band already opens on an ink slab and the marks
             below it already sit on their own tiles, so a filled plate would put
             a third surface in a band that has two, and a bordered one would draw
             a box around four groups that are not a box: there is neither a fill
             nor an edge here, only the cross between the quarters. A
             specification is a table, and a table is ruled rather than framed.

             Each head is a number, a name and a note. The notes are shared
             vocabulary rather than four sentences written here, because they say
             what Typeface and Tools mean on every case page, and they are looked
             up the way the story's kickers are. The two counts that used to close
             each head are gone: a tally restated what the reader can see by
             looking at the group, and the sentence says what the group is, which
             is the part the marks cannot state for themselves.

             Reading order is the DOM's, so the groups are written left to right
             and then down, and the numbering follows that order. The first two
             are hand-written because they are two different objects with two
             different notes, and the last two are a loop because the build's two
             groups are the same object twice. -->
        <div class="case-row case-spec" data-aos="fade-up">
          <div class="case-spec-group">
            <spec-head
              :index="1"
              :label="t('Typeface')"
              :note="t('The faces this build is set in.')"
            />
            <type-specimen :faces="project.fonts" />
          </div>

          <div class="case-spec-group">
            <spec-head
              :index="2"
              :label="t('Colour palette')"
              :note="t('The colours the brand is built from.')"
            />
            <swatch-list :swatches="palette" />
          </div>

          <!-- The build. The stack and the tools are names rather than
               sentences, and they are shown as the mark with its name: a reader
               who knows Laravel finds it by its mark before they read the word,
               and a mark is not a chip. The catalog assembles each entry's stack
               for it - the build the project was made on, then whatever that
               project adds - so the group arrives whole and nothing about which
               frameworks a project used is decided on this page.

               The names are brand names, so nothing here is translated; the marks
               are files the owner supplies, and one that has not landed yet
               leaves its name in place rather than dropping the entry, because a
               stack missing a line reads as a stack that does not use it. -->
          <div v-for="group in madeWith" :key="group.label" class="case-spec-group">
            <spec-head :index="group.index" :label="group.label" :note="group.note" />
            <logo-list :logos="group.logos" />
          </div>
        </div>
      </div>
    </section>

    <!-- 2. The live site, framed. The screenshot used to be the whole of this
         band, which showed the design but not the thing itself: a visitor could
         not scroll the real page, open a menu or see how it behaves. The frame
         is the client's own address now, and the screenshot behind it is the
         poster that shows while it loads.

         A site may refuse to be framed (X-Frame-Options and frame-ancestors are
         common on client sites), and a browser gives no reliable way to tell.
         So the band never depends on the frame: the address sits in the chrome,
         and a reader who wants the site on its own has it there. The button that
         used to open it from under the frame is gone, because the address above
         already states the same thing and a band does not need two doors to one
         page.

         The band is dark and the frame standing on it is light. As a lit panel
         against the band it reads as a window held up rather than a white box
         that has lost its edge on a white page.

         View in is the band's one control and the only thing on the page that
         changes the frame: each width it offers is the frame's own ceiling, so the
         address bar and the poster move with it. It offers only the devices the
         screen in front of the reader can tell apart, and on a phone it is not
         rendered at all, so it is never a control whose segments set the same
         width. The label and the switch sit between the head and the frame they
         act on, with one reveal over both, because a control that resizes
         something is read with it rather than before it.

         The head takes the band's own reveal and the body takes the one after
         it, which is the arrangement every other band on this page uses. It was
         the one head on the page carrying none, so the title appeared outright
         while the frame under it rose into place, which read as a missed step
         rather than a decision. -->
    <section class="case-preview dark">
      <div class="container">
        <section-title
          tone="dark"
          data-aos="fade-up"
          :kicker="t('The live site')"
          :title="t('See it running')"
          :lede="t('This is the client\'s own site, loaded in the frame below rather than shown as a picture of it. Scroll inside the frame to use the real page.')"
        />

        <div class="case-preview-body" data-aos="fade-up">
          <div v-if="viewOptions.length" class="case-view">
            <span class="case-view-label">{{ t('View in') }}</span>

            <segmented-switch
              :model-value="viewIndex"
              tone="dark"
              :options="viewOptions"
              @update:model-value="selectView"
            />
          </div>

          <browser-frame
            tone="light"
            :url="project.domain"
            :image="project.image"
            :src="project.url"
            :width="viewWidth"
            :secure="secure"
            :frame-title="t('The live site, framed on this page.')"
          />
        </div>
      </div>
    </section>

    <!-- 3. The story, in sections. It is the one band on the page that is read
         rather than scanned, so its body is the site's own long-form type: the
         `prose` owner's scale, asked for in its `prose--bare` variant because
         the chapter already supplies the ground the panel would draw. A panel
         inside a band is a card inside a card, so the reading sits on the
         chapter's own ground with no chrome around it. Nothing about a case
         study's reading is a new decision: the heading steps, the paragraph
         rhythm and the screen are the owner's, and only the panel is declined.
         The screen's caption is not the owner's either, because the screen is a
         browser frame: what it shows is said in the frame's own foot rather than
         under it, which is the one place on the page a `prose` figure does not
         carry its own words.

         The body is rows rather than one column of text: the prose in the
         leading column and the chapter's screens in the column beside it. The
         screens used to be interleaved between the paragraphs by a per-block
         index; in a two-column body there is nothing left to interleave, so the
         chapter's own screens stand beside its prose and the index that placed
         them is gone from the catalog.

         A chapter is one such row, and a long chapter is several: it states more
         than one screen, the page deals its paragraphs out between them, and the
         rows alternate so the screens fall left, right, left down the movement.
         That is the same mirroring the chapters already do to each other, run one
         step finer for the chapter whose reading is long enough to need it, and
         it is why the sides are decided from the chapter's own ground and then
         flipped per row: a chapter that changes sides when it changes ground
         keeps doing it, and a chapter that does it twice does it twice.

         The row is the client row above, not a new one: the same `row g-5` with
         `col-lg-*`. That is the page's own text-beside-a-column row and the two
         are read a band apart, so they share one mechanism rather than this band
         growing a second. It also gives what this row needs without a
         declaration: the 3rem gutter from $grid-gutter-width, and a stack below
         lg, which is where the head above it already reads as the start of the
         section. The page's other alternative, the `.case-spec` grid, splits two
         equal groups at lg and is a spec sheet of two halves, not a
         text-beside-media row, so it is not the sibling to copy. It splits at the
         same width this row does, which is the only thing the two have in common
         and is not a reason to share them.

         The one thing the two do not share is the cross-axis alignment, and it is
         the one thing they should not: a chapter's prose and its screen both open
         at the top of the row and the pair is read down together, so they start
         together, while the slab's two columns are two complete statements and are
         centred. Alignment is decided by what the columns are, not by which row
         they are in, so the utility differs where the content does and the two
         rows are otherwise the same row.

         The screen keeps the column beside the prose, and the two swap sides on
         every row. A case study read straight down a page is a list of paragraphs
         with somewhere to look; mirrored, it is a spread, and the eye has to
         cross the column it just finished to reach the next movement. The
         mirroring is tied to the tint below, so the chapter that changes ground
         is the chapter that changes sides, and a reader learns one rule instead
         of two.

         Every row is therefore a pair, because every row has a screen: there is
         no case where the prose takes the whole container, and no case where a
         movement is a paragraph about a website with no website in sight. One
         screen beside a chapter's reading is the norm and the other eleven
         projects are exactly that; a chapter that states several gets several,
         because the alternative is a dozen paragraphs beside one frame.

         Each subject is its own <section>, which is why a four-movement story
         names four sections in the outline rather than hiding them as h3s inside
         one. Each is a band in its own right, so each opens with the shared
         chrome: kicker, title, lede.

         The kicker is the movement's, not the band's. It used to be the band's
         name on every subject, which made the head identical to the client and
         live-site bands around it but told a reader nothing about the chapter
         under it: three sections in a row all announced as the story, when the
         one thing a chapter can say about itself before its heading is which
         movement of the story it is. So each movement announces itself, and the
         announcement is the catalog's rather than this page's, because a
         chapter about a beginning is that on every case page.

         The band therefore still carries no head of its own, and it must not grow
         one: one head above a set of heads would be the only head on the page
         with a band under it instead of a section. The subject head is the
         section head, so it is an h2 like every other band's title.

         The chapters alternate ground, and the alternation is anchored to the
         end of the list rather than the start. That is the one thing worth
         stating: the band under the story is the tinted foot band, so a chapter
         count that is even or odd has to land on the same ground either way or
         the page would run two tinted bands together on some projects and not on
         others. Counting back from the last chapter makes the last one white
         however many there are, so the tinted foot band always follows a white
         chapter and the story never meets it on the same ground. A tinted chapter
         also carries the blueprint lattice the site's other tinted bands carry,
         which is what makes the alternation read as a change of ground rather
         than a change of hex.
         Nothing here declares padding: every chapter takes the bare 8rem the
         global section rule gives, the same rhythm the rest of the page's bands
         use.

         The reveal is two blocks per row, not one: the head is a vertical stack
         member and rises, and the prose and its screen are a horizontal pair, so
         they converge from the edge they stand on. The same holds for every row
         of a long chapter, because a row is the unit that pairs rather than the
         chapter. A chapter carries nothing itself, because a reveal on the
         section would move its own tinted ground and open a seam at its edge.

         The subjects are the catalog's, not this page's, so a project with
         more to say carries more of them and the eleven shorter ones carry their
         four movements. Every chapter opens the same way, because the kicker and
         the lede are the movement's rather than the project's: a reader who has
         read one case page knows what the Background chapter is before reading
         the second, and the projects differ in what they say under it.

         The chapter number is the movement's place in the study, drawn as a
         quiet numeral in the rail beside the head with a rule after it. It is
         decoration and it is hidden from assistive tech, because the movement is
         named by its own heading and a reader who cannot see the numeral loses
         nothing: it says where in the story this is, not what it says.

         The chapter's screen is drawn as a frame with an empty slot in it until
         its file lands rather than dropped, so the story is the same shape from
         the first day and fills in as the artwork arrives. The prose is
         unaffected either way, and the slot says which site it will hold because
         the frame's chrome already carries the address. -->
    <section
      v-for="section in story"
      :key="section.head"
      class="case-chapter"
      :class="{ 'is-tint': section.tint }"
    >
      <div class="container">
        <!-- The chapter's own head. The numeral is the movement's place in the
             study and the rule beside it is what makes this read as a chapter
             rather than as another section title: it is decorative, so it is
             hidden from assistive tech and the section's own heading is what
             names it. The head is a vertical stack member, so it reveals up, and
             the two columns under it are a pair, so they converge. -->
        <div class="case-chapter-head">
          <span class="case-chapter-num" aria-hidden="true">{{ section.number }}</span>

          <section-title
            data-aos="fade-up"
            align="left"
            :kicker="t(section.kicker)"
            :title="t(section.head)"
            :lede="section.lede ? t(section.lede) : ''"
          />
        </div>

        <!-- The chapter's reading and its screens, a row per screen. A chapter
             that states one screen is one row, which is every movement of the
             other eleven projects; the long movement of a case study states
             several and becomes several rows, each pairing a group of
             paragraphs with the screen that illustrates them.

             The sides are the chapter's own, flipped per row: the row that
             follows the chapter's ground keeps the side the chapter would have
             taken on its own, and the next row takes the other one, so the
             chapter still changes sides when it changes ground and a long
             chapter carries the alternation on inside itself.

             The two columns are centred against each other rather than aligned
             at the top, which is the same choice the client slab above makes.
             The prose is the taller of the two and the screen is a fixed height,
             so at the top the frame sat in the upper half of the row with a tail
             of empty column under it while the reading ran on past it, and the
             pair read as a paragraph with a picture beside its first half. Centred,
             the screen sits against the middle of the argument it illustrates,
             which is where a figure belongs. -->
        <div
          v-for="block in section.blocks"
          :key="block.figure.file"
          class="case-block row g-5 align-items-center"
        >
          <div
            class="col-lg-6"
            :class="{ 'order-lg-2': block.mirror }"
            :data-aos="block.mirror ? 'fade-left' : 'fade-right'"
          >
            <div class="prose prose--bare">
              <p v-for="(paragraph, position) in block.paragraphs" :key="position">{{ t(paragraph) }}</p>
            </div>
          </div>

          <!-- The screen. Every row has one, because a row without the screen it
               is about is a paragraph about a website with no website in sight.
               A slot whose screenshot has not been supplied is drawn as an empty
               frame rather than dropped, so the chapter is the same height on the
               day it is written as on the day the artwork lands and the page
               never has to be re-laid-out around it. -->
          <div
            class="col-lg-6"
            :data-aos="block.mirror ? 'fade-right' : 'fade-left'"
          >
            <div class="prose prose--bare">
              <figure class="case-shot">
                <browser-frame
                  tone="light"
                  :url="project.domain"
                  :image="block.figure.src"
                  :image-alt="block.figure.caption ? t(block.figure.caption) : ''"
                  :caption="block.figure.caption ? t(block.figure.caption) : ''"
                  :height="shotHeight"
                  :zoomable="block.galleryIndex !== null"
                  @zoom="openGallery(block.galleryIndex)"
                />
              </figure>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- The foot band, in the shape Blog/Show.vue gives its related posts: the
         head and the grid inside one column, the head left-aligned, and the whole
         band absent when there is nothing to show. A case page and a post are the
         same kind of page, so the work at the foot of one is offered the way the
         writing at the foot of the other is. -->
    <section v-if="related.length" class="case-more">
      <div class="container">
        <div class="row">
          <div class="col">
            <section-title align="left" :title="t('More projects')" data-aos="fade-up" />

            <!-- A plain row, not the blog's `gy-4`. PortfolioCard carries its own
                 3rem below the card (it came out of the old `.project` with that
                 margin), so a row gutter here would be added to it and the foot
                 band would space wider than the listing and the home band, which
                 are the same cards in a plain row. PostCard has no margin of its
                 own, which is why the blog's row has to supply one.

                 And no `col-md-6` step, because this band is three cards: split
                 two-up it always strands the third, so below 992px the work
                 reads one per row. The blog's foot band is the same shape. -->
            <div class="row">
              <div
                v-for="item in related"
                :key="item.slug"
                class="col-lg-4"
                data-aos="fade-up"
              >
                <portfolio-card :project="item" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- The viewer. It is the page's, not a chapter's, because the run it walks
         is the whole study: a reader who opens the second chapter's first
         screenshot is offered the other four from anywhere in the story, which
         is the point of opening a set rather than one picture. It draws itself
         over the page and holds the run, the caption under each shot and the
         open position; this page only says which one was clicked. -->
    <lightbox v-model="galleryAt" :images="gallery" />
  </layout-public>
</template>

<style lang="scss" scoped>
@import '~/_variables.scss';

/* The bands alternate: the sheet at the top and the foot are tinted, the story
   alternates chapter by chapter, and the hero and the live frame are dark. Read
   in order that is dark, tint, dark, then tint, white and tint for the story's
   own run, and tint at the foot, so no two neighbours share a ground.

   The story is the only stretch that alternates inside itself, and it counts
   back from its last chapter so the chapter before the tinted foot band is
   always white however many chapters the project has. That is the chapter
   component's business below, not this list's, because the parity is only
   knowable from the count. What is here is the two bands whose ground never
   moves.

   The ground for each band is the only thing this block decides. The device
   frames carry their own shadows and the browser frame its own chrome, so
   neither needs anything from here. */
.case-glance,
.case-more {
  background: $ground-tint;
}

/* One row of the spec sheet's band. The band stacks three of them: the devices,
   the client with its facts, and the spec plate, which is the last row and holds
   the other four groups. Three rows with nothing between them read as one pile,
   so a row that stands between two plain rows carries a hairline and its own air
   above it.

   Two of the three are not plain, so neither carries the rule. The first opens
   the band and has nothing above it to be separated from, which is what `--lead`
   cancels. The slab is a surface in its own right and is the separation, so both
   it and the row under it drop the line; that is written below, where the slab
   is. */
.case-row {
  border-top: 1px solid $line-blue;
  margin-top: 3.5rem;
  padding-top: 3.5rem;
}

.case-row--lead {
  border-top: 0;
  margin-top: 0;
  padding-top: 0;
}

/* The QR card's own step under itself, so the client's name below reads as the
   next member of the column rather than as the card's caption. The card is the
   one element in the slab whose outer spacing is the page's business: everything
   else in the column is a title or a panel that carries its own rhythm.

   The card is a figure with no margin of its own, so without this the name would
   sit directly under the tile and the two would read as one object. */
.case-qr {
  margin-bottom: 1.75rem;
}

/* The one row that is a surface rather than a sheet of rules. The slab is what
   separates it from the row above, so it drops the hairline and keeps only the
   air: a rule over a dark slab would be a line drawn on the thing that already
   divides the band. Its own padding is the row's padding from here. */
.case-row--slab {
  border-top: 0;
  padding-top: 0;

  /* The slab owns the outer inset. A Bootstrap row pulls itself half a gutter out
     on each side so its columns line up with an un-padded container, which
     inside the slab would put the client's name on the slab's edge on a phone.
     Only the row inside a slab gives that back, and it gives back the horizontal
     half only: the row's vertical pull and the columns' own top margin cancel,
     so the first line stays where the slab's padding put it. */
  .row {
    margin-left: 0;
    margin-right: 0;
  }
}

/* The row after the slab. It drops the hairline, and with it the second half of
   the band's spacing.

   The line is what separates two plain rows of the band, and the row above this
   one is not plain: the slab is a dark surface standing on the band, so a rule
   below it is a line drawn under the thing that already divides the band. That
   is the same reasoning the slab uses to drop the rule above itself, so the band
   keeps one rule and it is the slab that both ends are read against.

   The padding follows from the same fact, and it is the part worth stating. A
   ruled row is spaced in two halves: 3.5rem of margin above its hairline and
   3.5rem of padding below it, so the rule sits halfway between the two bodies of
   content and 7rem is the real distance between them. Once the rule is gone the
   padding has nothing left to space - it was only there to hold the line off the
   content - so the row takes the margin alone. That is the same pair of values
   `.case-row--lead` sets for the same reason, and it is where the excess under
   the slab came from: the slab sat in 3.5rem of air above and 7rem below, so the
   gap the eye read as the slab's own bottom margin was the band's whole row
   rhythm doubled on one side only.

   The margin stays the band's own 3.5rem rather than stepping down, because the
   slab's air should be equal on both sides once the padding is gone. A surface
   that is told apart from its neighbours by its own edge still sits in the same
   space as any other row; what it does not need is a second half of that space
   holding a rule that was never drawn.

   Written as a sibling of the slab rather than as a class on the row, so both
   the dropped rule and the dropped padding follow the slab wherever the band puts
   it. */
.case-row--slab + .case-row {
  border-top: 0;
  padding-top: 0;
}

/* The spec plate: the four groups of the sheet, cross-ruled.

   It is one grid in both directions. One column and four stacked quarters below
   lg, where a group needs the band's whole measure - the type sample is a
   sentence in a face and the palette is a deck, and both are squeezed beside a
   sibling at half the band - and two columns from lg, which is where every other
   two-column row on this page splits.

   The rules are internal, and that is what makes four quarters read as one plate
   rather than as four cards. Each group after the first carries the rule above
   it, and in the second column each carries the rule to its left, so the cross is
   drawn by the cells that meet on it: there is no outer edge anywhere, because
   the plate has no edge to draw. The left column keeps the container's own edge,
   which is what lines the sheet up with the rows above and below it.

   That the rules come from the cells is why the gaps are zero and the air is
   padding instead. A grid gap would put a strip of nothing between two cells and
   the rule would then sit on one side of it, drawn against the gap rather than
   between the groups. With the gap at zero the cells touch, so a rule on one
   cell's edge is the boundary between it and its neighbour and it spans that
   cell's full height or width. The padding is what keeps the content off it, and
   it is the same on both sides so the rule sits on the line halfway between the
   two groups rather than belonging to either.

   The two insets are the band's own measures rather than new ones. Across, it is
   1.5rem on each side of the rule, which is half of the 3rem `$grid-gutter-width`
   the page's Bootstrap rows put between their columns, so the two columns of the
   plate are held apart by exactly what holds every other pair on the page apart.
   Down, it is 1.75rem on each side, which is half of the 3.5rem the band's own
   `.case-row` rhythm puts between its rows, so the plate's two quarters are a row
   apart by the same measure as the slab is from the sheet. A plate divided by a
   different step than the rows around it would read as a foreign object in the
   band.

   The staircase of `nth-child` selectors below is the cross written out, and the
   order of the blocks is load-bearing: the base rules are the stacked layout and
   the media query resets them, so the one-column rules cannot leak into the
   two-column one. The `-n + 2` and `n + 3` forms address the first and second
   rows, and the odd and even forms address the two columns, which works because
   the sheet is exactly two by two: at any other count the quarters would not be
   quarters and the rules would land on the wrong cells. */
.case-spec {
  display: grid;
  grid-template-columns: minmax(0, 1fr);

  .case-spec-group {
    border-top: 1px solid $line-blue;
    /* A column so a group can hand its spare height to the one thing in it that
       should fill it. Both groups in a row stretch to the taller of the two, and
       without this the swatch deck would sit at the top of its group and leave
       the rest of the cell empty. The deck asks for the leftover and ends level
       with the column beside it. The group is a block at every other width, so
       nothing here reaches the one-column sheet. */
    display: flex;
    flex-direction: column;
    padding-bottom: 1.75rem;
    padding-top: 1.75rem;

    /* The first group opens the plate and has nothing above it to be divided
       from, and the last closes it and has nothing below. */
    &:first-child {
      border-top: 0;
      padding-top: 0;
    }

    &:last-child {
      padding-bottom: 0;
    }
  }

  @media (min-width: 992px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    .case-spec-group {
      border-top: 0;
      padding: 0 1.5rem;
    }

    /* The left column holds the container's edge and the right column takes the
       rule between them. */
    .case-spec-group:nth-child(odd) {
      padding-left: 0;
    }

    .case-spec-group:nth-child(even) {
      border-left: 1px solid $line-blue;
      padding-right: 0;
    }

    /* The second row carries the rule and the air above it. */
    .case-spec-group:nth-child(n + 3) {
      border-top: 1px solid $line-blue;
      padding-top: 1.75rem;
    }

    /* The first row gives that rule its air from beneath. */
    .case-spec-group:nth-child(-n + 2) {
      padding-bottom: 1.75rem;
    }
  }
}

/* The label in front of the View in control.

   It is not the group label it used to share a class with. A group of the spec
   sheet is announced by `SpecHead` now, which owns the ruled line and the tally
   as well as the name, so this is the one label on the page that introduces a
   control rather than a body of content: it sits inline in front of the switch,
   in the same flex row, and it states what the switch chooses between.

   That is why it declares no bottom margin and takes the dark band's ink
   directly. Both used to be conditional, because the class was serving a group on
   a light band and a control on a dark one at once, and a label that has to ask
   which of the two it is is the drift the split removes. One job, one
   declaration, on the shared $label-* step. */
.case-view-label {
  color: $label-on-dark;
  font-family: $headings-font-family;
  font-size: $label-size;
  font-weight: $label-weight;
  letter-spacing: $label-tracking;
  text-transform: uppercase;
}

/* One chapter of the story, and the only band on the page whose ground is not
   fixed: every other band states its own and this one is tinted or plain
   depending on where it falls. The parity is counted back from the last chapter
   in the page's `story` computed, because the band after this run is white and
   the band after that is tinted, so the run has to end on the same ground on
   every project whatever the chapter count.

   A tinted chapter carries the blueprint lattice the site's other tinted bands
   carry, so the alternation reads as a change of ground rather than a change of
   hex. It is one hairline grid fading out near the foot of the band, which is
   the same lattice the services band draws; the band clips it, so nothing has to
   be sized to the content. */
.case-chapter {
  overflow: hidden;
  position: relative;
}

.case-chapter.is-tint {
  background: $ground-tint;

  &::before {
    background-image:
      radial-gradient(52rem 28rem at 100% 0%, $tint-primary-soft, transparent 66%),
      linear-gradient(rgba($text-heading, .045) 1px, transparent 1px),
      linear-gradient(90deg, rgba($text-heading, .045) 1px, transparent 1px);
    /* One position per layer, so the pointer moves the two line layers and
       leaves the glow at the corner it was placed in. */
    background-position: 0 0, 0 0, 0 0;
    background-size: auto, 4.5rem 4.5rem, 4.5rem 4.5rem;
    content: '';
    inset: 0;
    mask-image: linear-gradient(#000, rgb(0 0 0 / 0%) 88%);
    pointer-events: none;
    position: absolute;
    transition: background-position $dur-slow $ease;
    -webkit-mask-image: linear-gradient(#000, rgb(0 0 0 / 0%) 88%);
  }

  /* The lattice slides half a tile under the pointer, which is the accent the
     mobile page's app stage gives its own blueprint. Half a tile is the furthest
     the eye can travel while still reading the same grid, so the band gains a
     sense of depth without the ground appearing to jump to a new position, and
     it is $dur-slow because this is a ground moving rather than a control
     answering.

     The band is the target rather than anything inside it, because the lattice
     is the band's own ground: it is the same pairing as the stage, where the
     panel that carries the grid is the panel the pointer is over. There is no
     `:focus-within` half of it, unlike that stage, because a chapter holds
     nothing focusable: its frames are posters rather than live pages, so the
     only reader who can reach this band is one with a pointer. */
  &:hover::before {
    background-position: 0 0, 2.25rem 2.25rem, 2.25rem 2.25rem;
  }
}

/* The lattice is a layer of its own, so the chapter's content is lifted over it.
   Only the container is, because the lattice is a child of the band and the
   container is the one element that holds everything the chapter draws. */
.case-chapter > .container {
  position: relative;
  z-index: 1;
}

/* The rows a chapter's reading is split into. Every chapter has at least one and
   the head above it supplies the step under itself, so only the rows after the
   first need anything here: the step between two rows of reading is the page's
   own gutter, which is the same 3rem Bootstrap puts between the columns when a
   row stacks below lg. Giving the rows the gutter's own number is what keeps a
   chapter's stacked layout on a phone reading as one rhythm rather than two. */
.case-block + .case-block {
  margin-top: $grid-gutter-width;
}

/* The chapter's head: the numeral in a rail, a rule, then the shared title. The
   grid is what keeps the numeral out of the title block, so the section still
   opens with `SectionTitle` and nothing here re-declares a kicker or a heading.
   One column below md, because a numeral rail on a phone would spend a third of
   the width on decoration. */
.case-chapter-head {
  align-items: stretch;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: minmax(0, 1fr);
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    gap: 1.75rem;
    grid-template-columns: auto minmax(0, 1fr);
  }
}

/* The movement's place in the study. It is drawn as a quiet numeral rather than
   as type to read: it is at $line-strong, which is the lightest step that still
   reads as a mark on both grounds, and it is hidden from assistive tech because
   the heading beside it is what names the chapter. The rule is the rail's own
   right edge, and it stretches the height of the head because the grid item
   does: it is what makes the number read as a margin note rather than as a big
   character that happens to sit before an h2.

   It is centred in that height, and it declares its own centring rather than
   taking it from the head. `align-items: center` on `.case-chapter-head` would
   centre the rail against the title block and collapse the border to the height
   of the figure, which is the one thing the rail is for; centring inside the
   element moves the figure and leaves the rail stretching. The spec sheet's
   index is centred the same way, for the same reason.

   The size is the largest numeral on the site, and it is sized against the head
   beside it rather than against the other numerals: a chapter head is two or
   three lines tall, so at 2.6rem the figure held about a third of the rail and
   read as a tag at the top of a tall column. At 3.4rem it holds enough of the
   rail to answer the block it annotates, and the rail is what keeps it a margin
   note rather than a second heading: a numeral that size with nothing beside it
   would be a title. */
.case-chapter-num {
  align-items: center;
  color: $line-strong;
  display: flex;
  font-family: $headings-font-family;
  font-size: 3.4rem;
  font-weight: 700;
  letter-spacing: -0.05em;
  line-height: 1;

  @media (min-width: 768px) {
    border-right: 1px solid $line-blue;
    padding-right: 1.75rem;
  }
}

/* The frame is a figure of its own, and the prose owner puts its figure margin
   on every figure inside it. That rule is right for the slot in the column and
   wrong for the frame inside the slot, which is the frame's own box and takes
   the column's rhythm from its wrapper instead. Scoped through `:deep` because
   the frame's own root carries its own component's scope and no selector written
   here would otherwise reach it. */
.case-chapter :deep(figure.case-shot .browser-frame) {
  margin-bottom: 0;
  margin-top: 0;
}

/* The story row itself declares nothing, and that is the point of it. The
   reading is `prose`, asked for in its bare variant, so the heading steps, the
   paragraph rhythm and the slot all come from the owner and nothing is restated
   here. A chapter's caption rides inside the frame rather than under it, so there
   is nothing left to hang below the frame either. The row is the client row
   above, written in the template, so the two text-beside-a-column rows on this
   page cannot drift apart: same gutter, same split, same stack, and the same
   alignment, since both open at the top. The section head supplies the step
   under itself and the gutter supplies the step between the two columns once they
   stack, so there is no margin to set here either. */

/* The View in control: the label and the switch, centred under the band's head
   and above the frame they change, so that the control and the thing it acts on
   are read as one block. It is absent on a phone because the page does not render
   it there, not because this rule hides it: a phone cannot tell the device widths
   apart, so there is nothing for the control to switch.

   It wraps rather than overflowing, and the wrap is a guard rather than the shape
   the row is drawn in. From md up it is one line, because the switch is sized to
   its own labels and the widest the page makes it is the three segments offered at
   lg: 18.75rem of control plus the label, against a container of 960px. Both the
   label and the segments are translated, though, so the guard stays for whatever
   language or container makes that arithmetic tighter than it is in English. */
.case-view {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 1.75rem;
}
</style>

<script>
import { faArrowUpRightFromSquare, faCalendarDays, faDesktop, faGlobe, faIndustry, faLocationDot, faMobileScreenButton, faScrewdriverWrench, faTabletScreenButton } from '@fortawesome/free-solid-svg-icons';
import { library as FontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { useI18n } from 'vue-i18n';
import AppButton from '@/Components/AppButton.vue';
import BrowserFrame from '@/Components/BrowserFrame.vue';
import DeviceShowcase from '@/Components/DeviceShowcase.vue';
import InkSlab from '@/Components/InkSlab.vue';
import LayoutPublic from '@/Layouts/Public.vue';
import Lightbox from '@/Components/Lightbox.vue';
import LogoList from '@/Components/LogoList.vue';
import PageHero from '@/Components/PageHero.vue';
import PageMeta from '@/Components/PageMeta.vue';
import PanelList from '@/Components/PanelList.vue';
import PortfolioCard from '@/Components/PortfolioCard.vue';
import QrCard from '@/Components/QrCard.vue';
import SectionTitle from '@/Components/SectionTitle.vue';
import SegmentedSwitch from '@/Components/SegmentedSwitch.vue';
import SpecHead from '@/Components/SpecHead.vue';
import SwatchList from '@/Components/SwatchList.vue';
import TagList from '@/Components/TagList.vue';
import TypeSpecimen from '@/Components/TypeSpecimen.vue';
import { portfolioMessages } from '@/i18n/portfolios';

/* The icons this page draws. The three device widths the View in control can
   offer, plus the arrow the Link row's value carries. The lock is not here: it
   moved to the browser frame with the address it marks, and that component
   registers it. */
FontAwesomeLibrary.add(
  faDesktop,
  faTabletScreenButton,
  faMobileScreenButton,
  faArrowUpRightFromSquare,
  faIndustry,
  faLocationDot,
  faScrewdriverWrench,
  faCalendarDays,
  faGlobe
);

/* The three device widths the View in control can offer, keyed by the device they
   are the width of.

   One table rather than the two parallel lists this used to be, where the icons
   sat in `viewOptions` and the widths in `viewWidth` and the only thing holding
   them in step was that both happened to be written in the same order. The order
   a reader sees is the one `devicesFor` below returns, so this table does not
   need one of its own.

   Each width is a ceiling rather than a width, on a frame that is 100% of its
   container up to it, so a viewport narrower than the chosen device still fits
   the frame inside it. The desktop ceiling is a very wide length rather than
   "100%" because only lengths interpolate against each other: a percentage in
   the set would make the change from desktop to tablet a jump instead of the
   resize the control exists for.

   The label is the English key the three languages are looked up by, never the
   text a reader sees. */
const VIEW_DEVICES = {
  desktop: { icon: 'desktop', label: 'Desktop', width: '2000px' },
  tablet: { icon: 'tablet-screen-button', label: 'Tablet', width: '768px' },
  mobile: { icon: 'mobile-screen-button', label: 'Mobile', width: '390px' }
};

/* Bootstrap's md and lg, asked as media queries so this agrees with the
   stylesheet exactly rather than within a scrollbar's width of it. The site's
   own breakpoints are the same two, and the stylesheet spells the lower one as
   767.98px because a min-width query cannot name a fractional boundary from
   above.

   They are where the band's container, rather than the viewport, passes the
   ceiling of one of the devices above. That container is 720px at md against a
   tablet ceiling of 768px, so below lg the desktop segment would set the frame to
   the width the tablet segment already gives it. */
const VIEWPORT_LG = '(min-width: 992px)';

const VIEWPORT_MD = '(min-width: 768px)';

/* Which side of the two thresholds the viewport is on. Read when the page is
   built and again on every resize, so it is never a stale answer. */
function readViewport() {
  if (window.matchMedia(VIEWPORT_LG).matches) {
    return 'lg';
  }

  return window.matchMedia(VIEWPORT_MD).matches ? 'md' : 'sm';
}

/* Which devices a screen can tell apart, and the order it offers them in.

   All three above lg, because that is where the container is wider than the
   tablet ceiling and the desktop segment is a width the tablet one cannot reach.
   Two below it, because there the two would set the frame to the same width and a
   segment that changes nothing is a control lying about what it controls. None on
   a phone, where the control is not rendered at all: see the template, which
   reads this same list, so the answer here and the markup cannot disagree. */
function devicesFor(viewport) {
  if (viewport === 'lg') {
    return ['desktop', 'tablet', 'mobile'];
  }

  return viewport === 'md' ? ['tablet', 'mobile'] : [];
}

export default {
  components: {
    AppButton,
    BrowserFrame,
    DeviceShowcase,
    InkSlab,
    LayoutPublic,
    Lightbox,
    LogoList,
    PageHero,
    PageMeta,
    PanelList,
    PortfolioCard,
    QrCard,
    SectionTitle,
    SegmentedSwitch,
    SpecHead,
    SwatchList,
    TagList,
    TypeSpecimen
  },

  props: {
    /* One entry of PortfolioCatalog, already carrying its `devices`, its
       `study` chapters with their screens resolved, its merged stack and tools,
       and its `qr` path, plus the projects nearest it in the table, which
       PortfolioController wraps round the end for the foot band. */
    project: {
      type: Object,
      required: true
    },

    related: {
      type: Array,
      default: () => []
    }
  },

  data() {
    /* Read before the first render rather than in `mounted`, so a screen that
       cannot tell the three widths apart never draws a control it is about to be
       without. The device starts as the widest this screen offers, which is the
       one that shows the most. */
    const viewport = readViewport();

    return {
      /* Which device the frame is set to. A name rather than an index, because the
         control does not offer the same count on every screen and an index would
         name a different device on each one. It is page state rather than a prop
         because it changes nothing that is sent anywhere. */
      view: devicesFor(viewport)[0] || 'desktop',

      /* Which side of the two thresholds the viewport is on. */
      viewport,

      /* Which screenshot the viewer is open on, or null when it is closed.

         It is one number rather than a flag and an index, because the viewer
         open on nothing is not a state the reader can be in: the chapter sets
         this to a position in the run and the viewer's own close sets it back to
         null. That also means the page never has to know which shot is showing
         after the reader has walked the run, because the viewer says so. */
      galleryAt: null
    };
  },

  computed: {
    /* The trail is where the project is named now that the hero is generic, so
       it carries the domain rather than a business name: the catalog holds no
       business name, and the domain is what the cards, the sitemap and the
       document title all say. The last crumb is the page you are on, so it is
       the project, not the word "Case Study" the h1 prints. */
    crumbs() {
      return [
        { label: this.t('Home'), href: this.route('index') },
        { label: this.t('Portfolios'), href: this.route('portfolios') },
        { label: this.project.domain }
      ];
    },

    /* The case paragraph is keyed by the sentence the catalog holds, the same
       convention the whole site uses, so the copy for a project is one entry in
       one dictionary per language rather than text stored beside the data. */
    caseCopy() {
      return this.t(this.project.case);
    },

    /* The spec sheet. Every value but the year and the address is a key the
       dictionaries are looked up by, because "Education" and "Kuala Lumpur,
       Malaysia" are shared vocabulary across the case pages rather than one
       project's own words. The client is the band's title, so it is not repeated
       here.

       The address is the last row and the only one that is an action. It is not
       only the frame's chrome below: a reader who wants the site on its own
       should not have to scroll to the live band and read the bar to find out
       where it is, and a sheet of facts about a website is missing something if
       it cannot state the website. `href` is what marks the row as a link, so the
       template has one branch and the other four rows stay plain text. The value
       is the domain rather than the URL, because the scheme is the bar's job and
       the domain is what the catalog states.

       The service is the one value that is not the project's own. Every project
       in this catalog is work the studio did under a single service, so a row
       naming the shape each build happens to take - a portal, a shop, a product
       site - reads as four different services where the site sells five of which
       this is one, and the ledger would be answering "what is this" with a word
       from another question. The row states the service instead, and it states it
       in the vocabulary the header and the footer already print, so the ledger,
       the nav and the plan pages all name it the same way and no string was
       translated for this row.

       The catalog keeps each project's own build type. Nothing on this page
       renders it today, and it is kept deliberately: it is a fact about the
       project rather than about the page, and it is the field the ledger would
       read again if the row ever went back to naming the build. */
    facts() {
      return [
        { icon: 'industry', label: this.t('Industry'), value: this.t(this.project.fact.industry) },
        { icon: 'location-dot', label: this.t('Location'), value: this.t(this.project.fact.location) },
        { icon: 'screwdriver-wrench', label: this.t('Service'), value: this.t('Web design') },
        { icon: 'calendar-days', label: this.t('Year'), value: this.project.fact.year },
        { icon: 'globe', label: this.t('Link'), value: this.project.domain, href: this.project.url }
      ];
    },

    /* Whether the address the frame prints is served over TLS, which is what
       draws the lock and the scheme in its bar. It is read off the live URL
       rather than asserted, so an address that is not https would print bare. */
    secure() {
      return this.project.url.startsWith('https://');
    },

    /* The project's screens as one run, in the order the story tells them, and
       the run the viewer is handed.

       It is the story's figures and nothing else. The cover above them is a
       poster for the live frame rather than a screen of the build, and the three
       device captures are one page at three widths, each already framed at its
       own size by a band that can switch between them: a flat run that mixed the
       two in would lose the width each capture is of and make the reader count.
       What the chapters hold is what a reader is already flipping through, so
       that is what the viewer offers back.

       A figure whose screenshot has not been supplied is left out rather than
       drawn as a slot, because the viewer has no empty state: a blank window in
       the middle of a run of pictures is worse than a shorter run. */
    gallery() {
      return (this.project.study || [])
        .reduce((figures, section) => figures.concat(section.figures || []), [])
        .filter((figure) => figure.src)
        .map((figure) => ({
          file: figure.file,
          src: figure.src,
          caption: figure.caption ? this.t(figure.caption) : ''
        }));
    },

    /* Where each screen sits in that run, keyed by the figure's own file.

       The chapter has to hand the viewer a position rather than a picture, and
       the position is the run's, not the chapter's: the third figure of the
       second chapter is the fourth shot in the viewer. Keying it rather than
       counting it keeps the two orders independent, so a figure dropped from the
       catalog moves every later position without the chapter's own loop having
       to know that it did. */
    galleryIndex() {
      return this.gallery.reduce((index, shot, position) => {
        index[shot.file] = position;

        return index;
      }, {});
    },

    /* The chapters of the story, in the order they are read, each with its rows
       of paragraphs and the screens that stand beside them. A chapter whose
       screenshot has not been supplied still carries its screen, with an empty
       `src`, so the page never has to know whether one is coming: the catalog
       decides what exists and the frame draws a slot for what does not.

       A chapter is a head, the movement it recounts, and its reading paired with
       its screens. The head, the kicker and the lede are keys like every other
       string on the site, looked up here rather than as a block, because the body
       that prints them is the site's long-form type and it takes text rather than
       translated markup.

       The kicker and the lede arrive from the catalog rather than being written
       here, and that is the whole point of them: a chapter recounts a beginning,
       a problem or a build, and that is the same fact on every case page,
       so the announcement of it is stated once per movement beside the study
       vocabulary it belongs to instead of once per project. What this page
       decides is only how the movement reads.

       The pairing is the one decision the page makes about the reading, and it is
       made here rather than in the catalog because it is about how the prose
       sits beside the screens and not about the project. A chapter's paragraphs
       are dealt out between its screens in order, evenly, so a chapter with one
       screen keeps all of them in one row and a chapter with three gets three
       groups of about the same length.

       Two things are decided here besides, because they are about how the run
       reads and not about the project. `tint` is the chapter's ground, and it is
       also what mirrors the chapter: the alternation is counted back from the
       last chapter so the last one is always white, whatever the count, which is
       what keeps the tinted foot band that follows the story in step on
       every project. `mirror` is the row's own side, and the first row takes
       the chapter's so that the chapter's mirroring rule still holds: a chapter
       that is tinted still leads with its screen on the left, and each row after
       the first takes the other side. `number` is the chapter's place in the
       study, printed as the numeral in its rail. */
    story() {
      const total = this.project.study.length;

      return this.project.study.map((section, index) => {
        const tint = (total - index) % 2 === 0;
        const figures = section.figures || [];
        const paragraphs = this.paragraphGroups(section.body, figures.length);

        return {
          head: section.head,
          kicker: section.kicker,
          lede: section.lede || '',
          number: String(index + 1).padStart(2, '0'),
          tint,
          blocks: figures.map((figure, position) => ({
            figure,
            paragraphs: paragraphs[position],
            mirror: position % 2 === 0 ? tint : !tint,
            /* Where this screen sits in the viewer's run, or null when the
               chapter's screenshot has not been supplied and the run does not
               hold it. The frame takes the null as "there is nothing to open",
               which is what keeps the zoom mark off a slot. */
            galleryIndex: figure.file in this.galleryIndex ? this.galleryIndex[figure.file] : null
          }))
        };
      });
    },

    /* The categories the project is tagged with, in the words the catalog
       holds: the same six the card prints, looked up the way every other string
       on the page is. */
    badges() {
      return (this.project.badges || []).map((badge) => this.t(badge));
    },

    /* Two groups, side by side. The typefaces are not here: a face is part of
       the design language, not of the stack, and they belong beside the palette
       with the colours.

       The marks come from the catalog already paired with their names, so this
       hands the rows straight to LogoList rather than building a path per entry
       here.

       The index travels with the group rather than being counted in the template,
       because the two groups here are the third and fourth quarters of a plate
       whose first two are written above them: a number derived from this array's
       own position would say these are groups one and two. */
    madeWith() {
      return [
        {
          index: 3,
          label: this.t('Tech Stack'),
          note: this.t('The stack the build runs on.'),
          logos: this.project.stack
        },
        {
          index: 4,
          label: this.t('Tools'),
          note: this.t('The tools the work was drawn in.'),
          logos: this.project.tools
        }
      ];
    },

    /* The palette with its names translated. A colour's name is copy, so it is
       looked up like every other string; the hex beside it is a value and is
       printed as it is stored. */
    palette() {
      return this.project.palette.map((swatch) => ({
        hex: swatch.hex,
        name: this.t(swatch.name)
      }));
    },

    /* The three device captures. The alt is an English sentence with a
       placeholder in it, so it is looked up and interpolated like the rest of
       the copy, and the domain dropped into it is the same one the crumb and the
       document title print. An empty `src` is a capture that has not been
       supplied: the band draws that device as an empty slot at its own size
       rather than leaving it out of the row. */
    deviceShots() {
      return (this.project.devices || []).map((device) => ({
        name: device.name,
        src: device.src,
        alt: this.t(device.alt, { domain: this.project.domain })
      }));
    },

    /* Which devices the control offers, in the order it offers them. The rule is
       on `devicesFor` at the top of the file, next to the thresholds it is read
       against; this is the component's one read of it. */
    viewDevices() {
      return devicesFor(this.viewport);
    },

    /* The segments, in the order above. The icons are named rather than drawn,
       which is the shape SegmentedSwitch's marked segment expects, and each
       carries its name beside the mark: three device glyphs at this size are
       close enough to be told apart only by reading them, and the names are what
       the segments are for. */
    viewOptions() {
      return this.viewDevices.map((device) => ({
        icon: VIEW_DEVICES[device].icon,
        label: this.t(VIEW_DEVICES[device].label)
      }));
    },

    /* The selected segment, as the index the switch works in. The switch is a row
       of positions and the page's state is a device, so this is the one place the
       two meet. A view that is not in the current list cannot reach here, because
       the watcher below moves it whenever the list changes. */
    viewIndex() {
      return Math.max(0, this.viewDevices.indexOf(this.view));
    },

    /* The ceiling the frame is set to, which is the selected device's own. */
    viewWidth() {
      return VIEW_DEVICES[this.view].width;
    },

    /* How tall a chapter's screen stands. A crop of the top of a page rather
       than a window to scroll, because a chapter shows a still beside the
       paragraph it is explaining. It is one value for every chapter rather than
       one each, since a reader moving down the story should not find the same
       capture framed at two different heights. The live frame is the one caller
       that does not pass it, and it keeps the whole-window default. */
    shotHeight() {
      return 'clamp(15rem, 26vw, 24rem)';
    }
  },

  methods: {
    /* A chapter's paragraphs, dealt out between its screens, in order.

       Even groups rather than the first screen taking everything it can: the
       rows are read one after another, so four paragraphs beside each screen
       reads as a rhythm while twelve beside the first and none beside the rest
       reads as a chapter that ran out of pictures.

       The remainder goes to the leading groups, one each, because a reader
       arriving at a chapter meets its first screen soonest and the extra
       paragraph is better spent there than at the very end.

       One screen is the common case and it is not a division at all: the whole
       reading stays in one group, which is the row every movement of the other
       eleven projects draws. */
    paragraphGroups(paragraphs, count) {
      if (count <= 1) {
        return [paragraphs];
      }

      const size = Math.floor(paragraphs.length / count);
      const spare = paragraphs.length % count;
      const groups = [];
      let cursor = 0;

      for (let position = 0; position < count; position += 1) {
        const length = size + (position < spare ? 1 : 0);

        groups.push(paragraphs.slice(cursor, cursor + length));
        cursor += length;
      }

      return groups;
    },

    /* Open the viewer on one of the run's screens. The frame's zoom mark is only
       drawn when the chapter knows there is a position to open, so this is only
       ever called with one; the guard is here because a viewer opened on a
       position the run does not hold would be a window on nothing. */
    openGallery(index) {
      if (index < 0 || index >= this.gallery.length) {
        return;
      }

      this.galleryAt = index;
    },

    /* The switch reports a position and the page holds a device, so the two are
       translated here rather than at the call site. The guard is for the empty
       list: a control that is not rendered cannot be pressed, but a handler that
       turned a stray event into an undefined device would leave the frame at a
       width nothing claims. */
    selectView(index) {
      const device = this.viewDevices[index];

      if (device) {
        this.view = device;
      }
    },

    /* Re-read both thresholds rather than trusting which side of them a resize was
       seen from, so the state is a fact about the viewport rather than a tally. */
    syncViewport() {
      this.viewport = readViewport();
    }
  },

  /* The list of devices shrinks when the viewport crosses a threshold, and the
     device that was selected may not be in the smaller one: a reader who has the
     desktop width chosen and then narrows the window to a tablet would otherwise
     be left with a frame at a width no segment claims. Moving the selection onto
     the first of what is left is what keeps the frame and the control saying the
     same thing. A phone offers nothing to move to, and the frame there is held by
     its container, so the selection is left where it is. */
  watch: {
    viewDevices(devices) {
      if (devices.length && !devices.includes(this.view)) {
        this.view = devices[0];
      }
    }
  },

  /* The control is a function of the viewport, so a resize has to be able to
     change it. A media query is what the stylesheet uses and `readViewport`
     agrees with it exactly, but a query does not re-run on its own: this listener
     is what turns the crossing into state. It fires for every pixel of a drag, so
     the handler only ever writes a string that a comparison has already decided
     against, and a visit that changes nothing re-renders nothing. */
  mounted() {
    window.addEventListener('resize', this.syncViewport);
  },

  /* The listener is on the window, so it outlives the component unless it is taken
     off here. */
  beforeUnmount() {
    window.removeEventListener('resize', this.syncViewport);
  },

  setup() {
    const { t } = useI18n({ messages: portfolioMessages });

    return { t };
  }
};
</script>
